import React, { useState, useEffect } from 'react'
import {
  getSalas,
  getCategoriasUsadas,
  createSala,
  updateSala,
  deleteSala,
} from '../../lib/supabase'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import Loading from '../../components/Loading'

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
}

function CardSala({ sala, onSave, onDelete, podeEliminar }) {
  const [nome, setNome] = useState(sala.nome)
  const [editando, setEditando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [confirmarEliminar, setConfirmarEliminar] = useState(false)

  async function guardar() {
    if (!nome.trim() || nome === sala.nome) {
      setEditando(false)
      setNome(sala.nome)
      return
    }
    setGuardando(true)
    try {
      await onSave(sala.id, { nome: nome.trim() })
      setEditando(false)
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div style={{
      background: 'var(--cor-branco)',
      border: '1px solid var(--cor-borda)',
      borderRadius: 'var(--raio)',
      padding: '10px 14px',
      display: 'flex', alignItems: 'center', gap: '10px',
      flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: '16px' }} role="img" aria-hidden="true">🚪</span>
      {editando ? (
        <input
          autoFocus
          value={nome}
          onChange={e => setNome(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') guardar()
            if (e.key === 'Escape') { setNome(sala.nome); setEditando(false) }
          }}
          disabled={guardando}
          style={{ ...inputStyle, padding: '6px 10px', flex: 1, minWidth: '140px' }}
        />
      ) : (
        <span style={{ flex: 1, fontSize: '14px', minWidth: '140px' }}>{sala.nome}</span>
      )}

      {editando ? (
        <>
          <button
            onClick={guardar}
            disabled={guardando}
            style={{ background: 'var(--cor-principal)', color: 'white', border: 'none', borderRadius: 'var(--raio)', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', opacity: guardando ? 0.6 : 1 }}
          >
            {guardando ? 'A guardar…' : 'Confirmar'}
          </button>
          <button
            onClick={() => { setNome(sala.nome); setEditando(false) }}
            disabled={guardando}
            style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', color: 'var(--cor-texto-suave)' }}
          >
            Cancelar
          </button>
        </>
      ) : confirmarEliminar ? (
        <>
          <button
            onClick={() => { onDelete(sala.id); setConfirmarEliminar(false) }}
            style={{ background: 'var(--cor-principal)', color: 'white', border: 'none', borderRadius: 'var(--raio)', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
          >
            Confirmar
          </button>
          <button
            onClick={() => setConfirmarEliminar(false)}
            style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', color: 'var(--cor-texto-suave)' }}
          >
            Cancelar
          </button>
        </>
      ) : (
        <>
          <button
            onClick={() => setEditando(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--cor-texto-suave)' }}
          >
            Renomear
          </button>
          <button
            onClick={() => podeEliminar && setConfirmarEliminar(true)}
            disabled={!podeEliminar}
            title={podeEliminar ? 'Eliminar sala' : 'Tem de existir pelo menos uma sala por categoria com serviços'}
            style={{
              background: 'none', border: 'none',
              cursor: podeEliminar ? 'pointer' : 'not-allowed',
              fontSize: '12px',
              color: podeEliminar ? '#DC2626' : '#D1D5DB',
            }}
          >
            Eliminar
          </button>
        </>
      )}
    </div>
  )
}

function BlocoCategoria({ categoria, salas, categoriaUsada, onCreate, onUpdate, onDelete }) {
  const [novoNome, setNovoNome] = useState('')
  const [adicionando, setAdicionando] = useState(false)

  async function adicionar(e) {
    e.preventDefault()
    if (!novoNome.trim()) return
    setAdicionando(true)
    try {
      await onCreate({ nome: novoNome.trim(), categoria })
      setNovoNome('')
    } finally {
      setAdicionando(false)
    }
  }

  const podeEliminar = !categoriaUsada || salas.length > 1

  return (
    <div style={{
      background: 'var(--cor-creme)',
      border: '1px solid var(--cor-borda)',
      borderRadius: 'var(--raio-lg)',
      padding: '18px 20px',
      marginBottom: '14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px', gap: '10px' }}>
        <h3 style={{ fontSize: '16px', color: 'var(--cor-principal)' }}>{categoria}</h3>
        <span style={{ fontSize: '12px', color: 'var(--cor-texto-suave)' }}>
          {salas.length} sala{salas.length === 1 ? '' : 's'}{!categoriaUsada && ' · categoria sem serviços'}
        </span>
      </div>

      {salas.length === 0 ? (
        <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginBottom: '12px' }}>
          Sem salas — esta categoria não tem limite de capacidade (marcações simultâneas ilimitadas). Adiciona salas para limitar quantas marcações podem coincidir.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          {salas.map(s => (
            <CardSala
              key={s.id}
              sala={s}
              onSave={onUpdate}
              onDelete={onDelete}
              podeEliminar={podeEliminar}
            />
          ))}
        </div>
      )}

      <form onSubmit={adicionar} style={{ display: 'flex', gap: '8px' }}>
        <input
          type="text"
          placeholder="Nome da nova sala"
          value={novoNome}
          onChange={e => setNovoNome(e.target.value)}
          disabled={adicionando}
          style={{ ...inputStyle, flex: 1 }}
        />
        <button
          type="submit"
          className="btn-primario"
          disabled={!novoNome.trim() || adicionando}
          style={{ padding: '8px 16px', fontSize: '13px', minHeight: 'auto' }}
        >
          + Adicionar
        </button>
      </form>
    </div>
  )
}

export default function Salas() {
  const { tenant } = useTenant()
  const toast = useToast()
  const [salas, setSalas] = useState([])
  const [categoriasUsadas, setCategoriasUsadas] = useState([])
  const [carregando, setCarregando] = useState(true)

  function carregar() {
    if (!tenant) return
    setCarregando(true)
    Promise.all([getSalas(tenant.id), getCategoriasUsadas(tenant.id)])
      .then(([s, cs]) => {
        setSalas(s)
        setCategoriasUsadas(cs)
      })
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  }

  useEffect(() => { carregar() }, [tenant]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate(dados) {
    try {
      const novo = await createSala(tenant.id, dados)
      setSalas(prev => [...prev, novo])
      toast.erro(null)
    } catch (e) {
      toast.erro(e.message)
    }
  }

  async function handleUpdate(id, dados) {
    try {
      await updateSala(tenant.id, id, dados)
      setSalas(prev => prev.map(s => s.id === id ? { ...s, ...dados } : s))
      toast.erro(null)
    } catch (e) {
      toast.erro(e.message)
    }
  }

  async function handleDelete(id) {
    try {
      await deleteSala(tenant.id, id)
      setSalas(prev => prev.filter(s => s.id !== id))
      toast.erro(null)
    } catch (e) {
      toast.erro(e.message)
    }
  }

  const categoriasOrfas = [...new Set(salas.map(s => s.categoria))]
    .filter(c => !categoriasUsadas.includes(c))
  const todasCategorias = [...categoriasUsadas, ...categoriasOrfas]

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <h2 style={{ fontSize: '22px', marginBottom: '6px' }}>Salas</h2>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '28px', fontSize: '14px' }}>
        Cada categoria pode ter várias salas. A capacidade simultânea de marcações de uma
        categoria é igual ao número de salas registadas — mesmo que existam profissionais
        livres, se as salas estiverem todas ocupadas o horário não é oferecido aos clientes.
      </p>

      {carregando ? <Loading /> : todasCategorias.length === 0 ? (
        <p style={{ color: 'var(--cor-texto-suave)', textAlign: 'center', padding: '32px' }}>
          Sem categorias. Adicione serviços primeiro.
        </p>
      ) : (
        todasCategorias.map(cat => (
          <BlocoCategoria
            key={cat}
            categoria={cat}
            salas={salas.filter(s => s.categoria === cat)}
            categoriaUsada={categoriasUsadas.includes(cat)}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ))
      )}
    </div>
  )
}
